/**
 * OEM 組織管理者向けレイアウト
 *
 * lightmedia-admin-theme (lm-shell) 化済み。シェルの構成・ダークモード opt-in・
 * モバイルドロワー等の実装パターンは AdminLayout.tsx (/admin/* 側、フェーズ1で
 * 導入済み) を踏襲している。ブランドカラー/ロゴのみ OEM パートナーごとに
 * 差し替わる点が AdminLayout との違い。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  Activity,
  BarChart3,
  BookOpen,
  Building2,
  CreditCard,
  Film,
  Filter,
  FolderTree,
  ImageIcon,
  LayoutTemplate,
  Lock,
  Megaphone,
  MonitorSmartphone,
  Palette,
  Plug,
  Shield,
  ShieldAlert,
  SlidersHorizontal,
  TrendingUp,
  Users,
  UsersRound,
  Volume2,
  Music,
} from 'lucide-react';
import { brandConfig } from '../config/brands';
import { ScrollToTop } from '../components/routing/ScrollToTop';
import { BrandGlyph } from '../components/shared/BrandGlyph';
import { usePartnerBranding } from '../hooks/usePartnerBranding';
import { useImpersonation } from '../features/impersonation/useImpersonation';
import { useUser } from '../features/auth/useUser';
import { useLogout } from '../hooks/useLogout';
import AppLauncher from '../components/AppLauncher';
import './OrgAdminLayout.css';

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  note?: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    title: 'レポート',
    items: [
      { path: '/org-admin/reports', label: '収支レポート', icon: <BarChart3 size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/payment-report', label: '決済レポート', icon: <TrendingUp size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/usage-report', label: '利用状況レポート', icon: <Activity size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/funnel', label: 'ファネル分析', icon: <Filter size={16} strokeWidth={1.5} /> },
    ],
  },
  {
    title: 'コンテンツ設定',
    items: [
      { path: '/org-admin/attributes', label: '静止画 属性管理', icon: <FolderTree size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/media', label: '静止画 媒体管理', icon: <MonitorSmartphone size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/scenario-attributes', label: '動画シナリオ設定', icon: <Palette size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/scenario-other', label: '動画 その他の設定', icon: <SlidersHorizontal size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/actors', label: 'AI俳優管理', icon: <UsersRound size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/sound-effects', label: '効果音管理', icon: <Volume2 size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/free-bgm-tracks', label: 'フリー楽曲管理', icon: <Music size={16} strokeWidth={1.5} /> },
    ],
  },
  {
    title: '組織・メンバー',
    items: [
      { path: '/org-admin/organizations', label: '組織管理', icon: <Building2 size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/users', label: 'ユーザー管理', icon: <Users size={16} strokeWidth={1.5} /> },
    ],
  },
  {
    title: '監査',
    items: [
      { path: '/org-admin/materials', label: '素材監査', icon: <ImageIcon size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/scenarios', label: 'シナリオ監査', icon: <Film size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/impersonation', label: '代行アクセス監査', icon: <ShieldAlert size={16} strokeWidth={1.5} /> },
    ],
  },
  {
    title: 'サイト設定',
    items: [
      { path: '/org-admin/plans', label: 'プラン', icon: <CreditCard size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/site-settings', label: 'サイト設定', icon: <Palette size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/connections', label: '外部サービス接続', icon: <Plug size={16} strokeWidth={1.5} /> },
      { path: '/org-admin/announcements', label: 'お知らせ管理', icon: <Megaphone size={16} strokeWidth={1.5} /> },
    ],
  },
  {
    title: 'ヘルプ',
    items: [{ path: '/org-admin/manual', label: 'マニュアル', icon: <BookOpen size={16} strokeWidth={1.5} /> }],
  },
];

/** サイドバーのブランドロゴ。
 *  - logoDark (明色背景向け) があればライト時はそのまま、無ければ logoWhite に
 *    invert フィルタをかけて代用する (従来挙動を踏襲)。
 *  - ダーク時 (data-lm-theme="dark" / "auto"+OS dark) は logoWhite (暗背景向け)
 *    をそのまま表示する。表示切替は CSS 側 (OrgAdminLayout.css) で行う。
 *  - どちらの画像も無い (型上は logoWhite 必須だが防御的に) 場合は BrandGlyph +
 *    serviceName のフォールバックを表示する。 */
function BrandLogo() {
  const lightSrc = brandConfig.logoDark ?? brandConfig.logoWhite;
  const darkSrc = brandConfig.logoWhite ?? brandConfig.logoDark;
  const hasDedicatedLightLogo = Boolean(brandConfig.logoDark);

  if (!lightSrc && !darkSrc) {
    return (
      <span className="org-admin-brand-fallback">
        <BrandGlyph />
        <span>{brandConfig.serviceName}</span>
      </span>
    );
  }

  return (
    <>
      <img
        src={lightSrc}
        alt={brandConfig.serviceName}
        className={`org-admin-brand-logo org-admin-brand-logo-light${hasDedicatedLightLogo ? '' : ' org-admin-brand-logo-invert'}`}
      />
      {darkSrc && darkSrc !== lightSrc && (
        <img src={darkSrc} alt={brandConfig.serviceName} className="org-admin-brand-logo org-admin-brand-logo-dark" />
      )}
    </>
  );
}

/** アバターに表示するイニシャル (email 先頭2字を大文字で)。 */
function initialsFor(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return '?';
  return trimmed.slice(0, 2).toUpperCase();
}

function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return isActive ? 'lm-nav-link active' : 'lm-nav-link';
}

export default function OrgAdminLayout() {
  const location = useLocation();
  usePartnerBranding(true);

  // 代行中は org-admin 配下にアクセスできない (バックエンド側 prefix guard で 403)。
  // フロント側でも navigate を抑止して空テーブル等の中途半端な表示を避ける。
  const { isImpersonating, isLoading: impLoading } = useImpersonation();
  // partner owner org の admin のみ /org-admin/* を利用可。顧客 org の admin は
  // 自組織のみ管理する設計なので partner 横断管理ページには入れない。
  const { isAuthenticated, isPartnerAdmin, isPlatformOrg, user } = useUser();

  // バナープロンプト閲覧は LightMedia (platform) 組織限定。
  // platform のときだけ「コンテンツ設定」グループ末尾にナビ項目を足す。
  const visibleNavGroups = useMemo<NavGroup[]>(() => {
    if (!isPlatformOrg) return navGroups;
    return navGroups.map((group) =>
      group.title === 'コンテンツ設定'
        ? {
            ...group,
            items: [
              ...group.items,
              {
                path: '/org-admin/banner-prompts',
                label: 'バナープロンプト',
                icon: <LayoutTemplate size={16} strokeWidth={1.5} />,
              },
            ],
          }
        : group,
    );
  }, [isPlatformOrg]);

  /** pathname からヘッダ左のページタイトルを導出する (nav 項目のラベルをそのまま使う)。 */
  const pageTitle = useMemo(() => {
    for (const group of visibleNavGroups) {
      for (const item of group.items) {
        if (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)) {
          return item.label;
        }
      }
    }
    return `${brandConfig.serviceName} 管理画面`;
  }, [visibleNavGroups, location.pathname]);

  // モバイル (≤900px) のサイドバードロワー開閉。.lm-sidebar / .lm-scrim への
  // .is-open 付け替えはアプリ側の責務 (lightmedia-admin-theme の UX 規約)。
  // デスクトップの折りたたみ機能は lm-shell の契約外のため廃止した (/admin と同判断)。
  const [drawerOpen, setDrawerOpen] = useState(false);
  // ヘッダ右上のマイページメニュー (.lm-menu-anchor) の開閉。
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement>(null);

  // ルート遷移のたびにモバイルドロワーは閉じる。
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // マイページメニューは外側クリックで閉じる。
  useEffect(() => {
    if (!menuOpen) return;
    function handlePointerDown(e: MouseEvent) {
      if (menuAnchorRef.current && !menuAnchorRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [menuOpen]);

  const closeDrawer = () => setDrawerOpen(false);

  // document.title は routes.tsx の handle.titleSuffix ('{serviceName} 管理画面')
  // が定義元。DocumentTitleSync が解決する。

  // /org-admin/* 配下だけ lightmedia-admin-theme のダーク値を OS 設定に追従させる
  // (opt-in)。<html> に data-lm-theme を常時付与すると /org-admin 以外 (通常ユーザー
  // 向けダーク UI) の color-scheme まで変わってしまうため、マウント中だけ 'auto' を
  // 設定し、アンマウント時に必ず削除する (AdminLayout と同じ契約)。
  useEffect(() => {
    document.documentElement.dataset.lmTheme = 'auto';
    return () => {
      delete document.documentElement.dataset.lmTheme;
    };
  }, []);

  // iOS Safari の URL bar 周辺や overscroll で見える上下領域は <body> の背景色を
  // 反映する (App.css の body { background: #000 } が黒帯の正体)。OrgAdminLayout
  // 表示中だけ <html> / <body> の背景色と <meta name="theme-color"> を --lm-bg
  // (上の effect で data-lm-theme="auto" 適用済み。OS がダーク設定ならダーク値になる)
  // に合わせる。アンマウント時に全て元へ戻す。
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const previousRootBg = root.style.backgroundColor;
    const previousBodyBg = body.style.backgroundColor;
    const lmBg = getComputedStyle(root).getPropertyValue('--lm-bg').trim() || '#fafafa';
    root.style.backgroundColor = lmBg;
    body.style.backgroundColor = lmBg;

    let themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const createdMeta = !themeColorMeta;
    const previousThemeColor = themeColorMeta?.content ?? '';
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.content = lmBg;

    return () => {
      root.style.backgroundColor = previousRootBg;
      body.style.backgroundColor = previousBodyBg;
      if (createdMeta) {
        themeColorMeta?.remove();
      } else if (themeColorMeta) {
        themeColorMeta.content = previousThemeColor;
      }
    };
  }, []);

  const handleLogout = useLogout();

  if (!impLoading && isImpersonating) {
    return <Navigate to="/" replace />;
  }
  if (isAuthenticated && !isPartnerAdmin) {
    return <Navigate to="/" replace />;
  }

  const navItemTitle = (label: string, note?: string) => (note ? `${label} (${note})` : label);

  return (
    <div className="lm-shell admin-layout org-admin-layout">
      <ScrollToTop />
      <div className={drawerOpen ? 'lm-scrim is-open' : 'lm-scrim'} onClick={closeDrawer} />

      <aside className={drawerOpen ? 'lm-sidebar is-open' : 'lm-sidebar'}>
        <div className="lm-sidebar-brand">
          <Link to="/" title={brandConfig.serviceName} className="org-admin-brand-link">
            <BrandLogo />
          </Link>
        </div>
        <nav className="lm-nav">
          {visibleNavGroups.map((group) => (
            <div key={group.title} className="lm-nav-group">
              <div className="lm-nav-group-label">{group.title}</div>
              {group.items.map((item) =>
                item.disabled ? (
                  <span
                    key={item.path}
                    className="lm-nav-link is-disabled"
                    title={navItemTitle(item.label, item.note ?? '準備中')}
                    aria-disabled="true"
                  >
                    {item.icon}
                    {item.label}
                    <Lock size={12} strokeWidth={1.75} className="org-admin-nav-lock" />
                  </span>
                ) : (
                  <NavLink key={item.path} to={item.path} className={navLinkClassName} onClick={closeDrawer}>
                    {item.icon}
                    {item.label}
                  </NavLink>
                ),
              )}
            </div>
          ))}
          {isPlatformOrg && (
            <div className="lm-nav-group">
              <NavLink to="/admin" className={navLinkClassName} onClick={closeDrawer}>
                <Shield size={16} strokeWidth={1.5} />
                運営管理画面
              </NavLink>
            </div>
          )}
        </nav>
      </aside>

      <div className="lm-main">
        <header className="lm-header">
          <button
            type="button"
            className="lm-menu-toggle"
            aria-label="メニューを開く"
            onClick={() => setDrawerOpen((v) => !v)}
          >
            ☰
          </button>
          <h1 className="lm-page-title">{pageTitle}</h1>
          <div className="lm-header-actions">
            <Link to="/" className="lm-btn lm-btn-ghost lm-btn-sm">
              アプリに戻る
            </Link>

            <AppLauncher />

            <div className={menuOpen ? 'lm-menu-anchor is-open' : 'lm-menu-anchor'} ref={menuAnchorRef}>
              <button
                type="button"
                className="lm-avatar"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="マイページメニュー"
              >
                {user?.email ? initialsFor(user.email) : '?'}
              </button>
              <div className="lm-menu">
                {user?.email && (
                  <div className="lm-menu-item" style={{ cursor: 'default', fontWeight: 600 }}>
                    {user.email}
                    <span style={{ display: 'block', fontWeight: 400, color: 'var(--lm-muted)', fontSize: 12 }}>
                      {isPlatformOrg ? 'platform admin' : 'partner admin'}
                    </span>
                  </div>
                )}
                <div className="lm-menu-separator" />
                <button
                  type="button"
                  className="lm-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    handleLogout();
                  }}
                >
                  ログアウト
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="lm-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
